/**
 * WorkoutGroupField Use Case Hook
 * Manages workout array within a workout group using React Hook Form
 * Requirements: 1.3, 4.4, 6.1, 6.2
 */

import {useState, useCallback} from 'react';
import {useFieldArray, UseFormReturn} from 'react-hook-form';
import type {
  WorkoutRoutineFormData,
  WorkoutFormData,
} from '../../WorkoutRoutineForm.schema';
import {
  createDefaultWorkout,
  isPersistedItem,
} from '../../WorkoutRoutineForm.schema';
import type {
  UseWorkoutGroupFieldReturn,
  MediaType,
} from './WorkoutGroupField.type';
import type {FileProps} from '../../../../../types';

/**
 * Hook for managing workouts within a workout group
 * Implements useFieldArray for the workouts array
 *
 * @param form - React Hook Form instance
 * @param groupIndex - Index of the parent workout group
 * @returns Workout field array and action handlers
 */
export function useWorkoutGroupField(
  form: UseFormReturn<WorkoutRoutineFormData>,
  groupIndex: number,
): UseWorkoutGroupFieldReturn {
  const [isExpanded, setIsExpanded] = useState(true);

  // useFieldArray for managing workouts within this group
  // Requirements: 1.2 - useFieldArray for managing dynamic collections
  const workoutsField = useFieldArray({
    control: form.control,
    name: `workoutGroups.${groupIndex}.workouts`,
    keyName: 'fieldId',
  });

  /**
   * Toggle expanded/collapsed state
   */
  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  /**
   * Add a new workout to this group
   * Requirements: 4.4 - append to workouts array
   */
  const addWorkout = useCallback(() => {
    const newWorkout = createDefaultWorkout();
    workoutsField.append(newWorkout);

    // Focus the new workout's name input after render
    setTimeout(() => {
      const newIndex = workoutsField.fields.length;
      const element = document.getElementById(
        `workoutGroup-${groupIndex}-workout-${newIndex}`,
      );
      if (element) {
        element.focus();
      }
    }, 100);
  }, [workoutsField, groupIndex]);

  /**
   * Remove a workout from this group
   * Requirements: 4.3 - mark with _destroy if persisted, or remove if new
   *
   * @param index - Index of the workout to remove
   */
  const removeWorkout = useCallback(
    (index: number) => {
      const workout = form.getValues(
        `workoutGroups.${groupIndex}.workouts.${index}`,
      );

      if (isPersistedItem(workout)) {
        // Mark persisted items with _destroy flag
        form.setValue(
          `workoutGroups.${groupIndex}.workouts.${index}._destroy`,
          true,
        );
      } else {
        // Remove new items directly
        workoutsField.remove(index);
      }
    },
    [form, workoutsField, groupIndex],
  );

  /**
   * Move a workout from one position to another
   * Requirements: 4.6 - support reordering items
   *
   * @param from - Source index
   * @param to - Destination index
   */
  const moveWorkout = useCallback(
    (from: number, to: number) => {
      const workouts = form.getValues(`workoutGroups.${groupIndex}.workouts`);

      // Validate indices
      if (
        from < 0 ||
        from >= workouts.length ||
        to < 0 ||
        to >= workouts.length ||
        from === to
      ) {
        return;
      }

      workoutsField.move(from, to);
    },
    [form, workoutsField, groupIndex],
  );

  /**
   * Handle media file updates for the workout group
   *
   * @param mediaType - Type of media (cover, video, audio, attachment)
   * @param file - File data
   */
  const handleMediaUpdate = useCallback(
    (mediaType: MediaType, file: FileProps) => {
      const fieldMap: Record<MediaType, string> = {
        cover: 'coverUrl',
        video: 'videoUrl',
        audio: 'audioUrl',
        attachment: 'attachmentUrl',
      };

      const fieldName = fieldMap[mediaType];
      if (fieldName) {
        form.setValue(`workoutGroups.${groupIndex}.${fieldName}` as any, file, {
          shouldDirty: true,
        });
      }
    },
    [form, groupIndex],
  );

  return {
    workoutsField,
    isExpanded,
    toggleExpanded,
    addWorkout,
    removeWorkout,
    moveWorkout,
    handleMediaUpdate,
  };
}

export default useWorkoutGroupField;
